import {
  ChangeEventHandler,
  Dispatch,
  MouseEventHandler,
  SetStateAction,
  useState
} from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { useField } from 'formik'
import noop from 'lodash/noop'

import { colors, transitions } from '@/styles'
import { PasswordMasked, PasswordVisible } from '@/components/icons'

import FieldInput from './field-input'

const Container = styled.div<{ hasIcon?: boolean }>`
  position: relative;
  ${props =>
    props.hasIcon &&
    `
    > div {
      > input {
        width: calc(100% - 27px);
        padding-right: 3px !important;
        border-right: 0;
        border-radius: 6px 0 0 6px;
      }
    }
  `}
`

const Button = styled.button<{ focused?: boolean; hasError?: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  height: 60px;
  width: 32px;
  line-height: 1;
  padding: 5px 2px 0 0;
  cursor: pointer;
  border-radius: 0 6px 6px 0;
  border: 1px solid ${colors.grey};
  border-left: 0;
  transition: border-color ${transitions.ease};
  background: ${colors.white};

  ${props =>
    props.focused &&
    `
    border-color: ${colors.secondary};
  `}
  ${props =>
    props.hasError &&
    `
    border-color: ${colors.red};
  `}
`

interface ToggleProps {
  visible?: boolean
  width?: number
  height?: number
  show?: boolean
  focused?: boolean
  hasError?: boolean
  onClick: MouseEventHandler<HTMLButtonElement>
}

const ToggleVisible: React.FC<ToggleProps> = ({
  visible,
  width = 25,
  height = 25,
  show,
  ...rest
}) => {
  const { t } = useTranslation()
  const InnerIcon = visible ? PasswordMasked : PasswordVisible
  if (!show) return null
  return (
    <Button
      {...rest}
      type="button"
      aria-pressed={visible}
      aria-label={t(
        'b.components.password_field.toggle.label',
        'Show password'
      )}
    >
      <InnerIcon width={width} height={height} />
    </Button>
  )
}

interface InputBaseProps extends React.InputHTMLAttributes<HTMLInputElement> {
  setVisible?: Dispatch<SetStateAction<boolean>>
  visible?: boolean
  name: string
  hideError?: boolean
  onChange?: ChangeEventHandler<HTMLInputElement>
}

const PasswordInputBase: React.FC<InputBaseProps> = ({
  setVisible,
  visible,
  name,
  hideError,
  onBlur = noop,
  onFocus = noop,
  ...rest
}) => {
  const [, meta, helpers] = useField(name)
  const [focused, setFocused] = useState<boolean | undefined>()
  const hasError = !!meta.error && meta.touched && !hideError
  const onClick = () => setVisible && setVisible(value => !value)
  return (
    <Container hasIcon={!!setVisible}>
      <FieldInput
        {...rest}
        hideError={hideError}
        name={name}
        type={visible ? 'text' : 'password'}
        onBlur={() => {
          onBlur()
          helpers.setTouched(true)
          setFocused(false)
        }}
        onFocus={() => {
          onFocus()
          setFocused(true)
        }}
      />
      <ToggleVisible
        visible={visible}
        onClick={onClick}
        show={!!setVisible}
        focused={focused}
        hasError={hasError}
      />
    </Container>
  )
}

const PasswordInputWithState: React.FC<InputBaseProps> = props => {
  const [visible, setVisible] = useState(false)
  return (
    <PasswordInputBase {...props} visible={visible} setVisible={setVisible} />
  )
}

const PasswordInput: React.FC<InputBaseProps> = props => {
  if ('visible' in props) return <PasswordInputBase {...props} />
  return <PasswordInputWithState {...props} />
}

export default PasswordInput
